import { Flex, Text, Accordion, Heading, Link } from '@chakra-ui/react';
import NextLink from 'next/link';

import QUESTION_MARK_ICON from '@/assets/icons/ic-question-mark.svg';

import { AccordionItemResearchers } from '@/components/ui/features/researchers/ResearchersFAQ/AccordionItemResearchers';

export const ResearchersFaq = () => {
  return (
    <Flex flexDirection={{ base: 'column', md: 'row' }} mt={12} gap={{ base: 8, md: 12 }}>
      <Flex gap={4} flexDirection="column">
        <QUESTION_MARK_ICON />
        <Heading whiteSpace="normal">Frequently Asked Questions</Heading>
        <Text>
          Still have more questions?
          <br />
          <br />
          We’re happy to help.
          <br />
          Email us:{' '}
          <Link variant="highlight" href="mailto:support@underline.io">
            support@underline.io
          </Link>
          <br />
          Visit: {` `}
          <NextLink href="/submit">
            <Text as="span" color="violet.400">
              www.underline.io/submit
            </Text>
          </NextLink>
        </Text>
      </Flex>
      <Accordion className="researchers-accordion" maxW="795px" w="full" allowToggle>
        <AccordionItemResearchers title="Why should I pay to publish a presentation I already gave?">
          Here are two great reasons:
          <br />
          <br />
          1. How many people attended the conference? 10x that number, or even 100x that number are people who search
          and use the Underline Library.
          <br />
          <br />
          Think you have visibility at the conference? In our experience, more people engage with the talk AFTER the
          conference from our library than during the days of the conference.
          <br />
          <br />
          The Underline Library is a great place to be found! It is what researchers around the world are using to find
          talks from conferences they did not attend! It’s a great way for potential collaborators to find you and
          propose interesting ideas and research projects.
          <br />
          <br />
          2. Most conferences don’t provide long-term hosting or discoverability for individual talks. By submitting
          your presentation to Underline, you give it a <strong>permanent home with a DOI</strong>, structured metadata,
          searchable transcripts, and global discoverability through libraries, search engines, and academic databases —
          all for just <strong>$19.95</strong>. Think of it as investing in the <strong>long-term impact</strong> of
          work you’ve already completed.
          <br />
          <br />
          Bottom line: It is the best way to showcase your work and be found!
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Can’t I just upload my video to YouTube for free?">
          YouTube isn’t designed for academic research. Researchers do NOT go to Youtube to find interesting research.
          <br />
          <br />
          Underline provides a <strong>scholarly environment</strong> where your presentation is hosted alongside your
          {` `}
          <strong>paper, slides, poster, and abstract</strong>, enriched with a DOI, and indexed by platforms like
          Google Scholar and institutional library systems — capabilities YouTube doesn’t provide.
          <br />
          <br />
          And no cat videos.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Is this peer-reviewed? Will it hurt my academic reputation?">
          No. Underline is <strong>not a peer-reviewed journal</strong>, and will not be viewed as self-publishing. We
          host <strong>conference talks that have already gone through peer review</strong>. Our platform{' '}
          <strong>complements traditional publishing</strong> by enhancing discoverability, not replacing it.
          <br />
          <br />
          Underline is where researchers come to find new ideas, to find people working in fields where they are looking
          for collaborators - and with the video library, a fantastic showcase for your work.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Will anyone actually see my presentation?">
          Yes. Our library is used by thousands of researchers, librarians, and institutions worldwide. Underline
          content is indexed in major search engines and academic discovery tools, and analytics show that well-packaged
          talks continue to receive views, citations, and collaboration inquiries long after the conference has ended.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Can’t I just link to my slides and video from my personal website?">
          Sure you can. However, unless you are an SEO wiz and get tons of traffic, it will be hard to find
          <br />
          <br />
          Underline is digital academic library. It is curated. It is in many of the top research libraries and
          universities around the world. It uses the latest SEO to drive additional traffic from Google Scholar.
          <br />
          <br />
          Underline provides an <strong>official, durable academic record</strong> that’s easily cited and shared—and
          appears in institutional research portals and discovery tools.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Is this permanent? Will my talk still be available in 5 years?">
          Yes. All accepted presentations are <strong>archived with a persistent DOI</strong> and stored in a long-term
          infrastructure designed for academic preservation and access.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Do I get analytics or engagement data?">
          Yes. We provide <strong>basic analytics</strong> such as view counts, geographic reach, and engagement
          patterns—so you can track how your work is being accessed and used.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="My conference already published the paper and video — isn’t this redundant?">
          Not really. While your conference may host recordings or proceedings, Underline offers a{' '}
          <strong>fully packaged media container</strong>—video, slides, paper, abstract, poster—with a DOI, searchable
          transcripts, metadata, and academic indexing for{' '}
          <strong>broader discoverability and sustained engagement</strong>.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Is it difficult to submit? I don’t have time.">
          Submitting takes <strong>under 10 minutes</strong>. Simply upload your materials (or provide a video link),
          complete a few metadata fields, and we handle the rest—transcription, DOI registration, formatting, and
          indexing.
        </AccordionItemResearchers>
        <AccordionItemResearchers title="Will this interfere with journal submissions or copyright?">
          No. Sharing your conference presentation (a public-facing talk, not a journal manuscript) does{' '}
          <strong>not conflict with journal submission policies</strong>. Many researchers use Underline to showcase
          preliminary work that later evolves into formal publications.
        </AccordionItemResearchers>
      </Accordion>
    </Flex>
  );
};
